import React, { useCallback, useEffect, useState } from "react";
import { sendAnalyticsPageViewEvent } from "../../utils/adobeDataLayerUtils";
import { useAcid } from "../../hooks/useAcid";
import {
  MembershipTier,
  DestinationSearch,
  PassionCarousel,
  HotelsCarousel,
  MomentPassion,
  HeroPassion,
  CollectionsModule,
  JoinBonvoyPopup,
} from "../../components";
import { usePassionsData } from "../../hooks/usePassionsData";
import { usePassionsHubDestination } from "../../hooks/usePassionsHubDestination";
import { usePassionsHubHotels } from "../../hooks/usePassionsHubHotels";
import { useProfileStore, usePassionsStore } from "../../store";
import { useShallow } from "zustand/react/shallow";
import styles from "./PassionsHub.module.css";
import { HobbyCarousel } from "../../components/HobbyCarousel";

export const PassionsHub = () => {
  const { acid } = useAcid();
  const [showJoinBonvoyPopup, setShowJoinBonvoyPopup] = useState(false);
  const { profile, getSelectedPassions, refetch } = usePassionsData();

  // Get membership tier data from the profile store
  const getMembershipTierData = useProfileStore(
    (state) => state.getMembershipTierData,
  );
  const profileLoading = useProfileStore((state) => state.loading);
  const membershipTierData = getMembershipTierData();

  // Get selected passions from the passions store for reactive UI updates
  // Use useShallow to properly detect array content changes
  const storeSelectedPassions = usePassionsStore(
    useShallow((state) => state.selectedPassions),
  );
  const setSelectedPassions = usePassionsStore(
    (state) => state.setSelectedPassions,
  );

  // Use store passions if available (for guest selections), otherwise use profile passions
  const profilePassions = getSelectedPassions();
  const selectedPassions =
    storeSelectedPassions.length > 0 ? storeSelectedPassions : profilePassions;

  // Sync profile passions to store when they load
  useEffect(() => {
    if (profilePassions.length > 0 && storeSelectedPassions.length === 0) {
      setSelectedPassions(profilePassions);
    }
  }, [profilePassions, storeSelectedPassions.length, setSelectedPassions]);

  // Get destination data from user's passions using the hook
  const {
    destination: passionDestination,
    destinationName,
    imageUrl: destinationImageUrl,
  } = usePassionsHubDestination(selectedPassions, profileLoading && !!acid);

  // Fetch hotels data including recently viewed property
  const recentlyViewedPropertyId = profile?.lastSearchDestination; //|| "KOAWI";
  const { recentlyViewedProperty, loading: hotelsLoading } =
    usePassionsHubHotels([], {
      recentlyViewedPropertyId,
      limit: 1,
    });

  // Use passion-based destination (matches the image) - this ensures the destination input matches the hero image
  const initialDestination = destinationName;
  const currentPassion =
    passionDestination?.passion ||
    (selectedPassions.length > 0 ? selectedPassions[0] : null);

  // Show collections module when:
  // 1. User is not logged in (no acid), OR
  // 2. User is logged in AND profile has loaded AND has no passions from profile
  // Don't show while loading to avoid flickering
  const hasNoProfilePassions = profilePassions.length === 0;
  const isProfileLoaded = !profileLoading && (profile !== null || !acid);
  const showCollectionsModule =
    !acid || (isProfileLoaded && hasNoProfilePassions);

  const handleSaveSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    // Send analytics page view event
    sendAnalyticsPageViewEvent("Passions Hub Page", 'Passions-Collection');
    // Show Join Bonvoy popup when user is not logged in
    if (!acid) {
      setShowJoinBonvoyPopup(true);
    }
  }, [acid, profilePassions, membershipTierData]);

  const onCloseJoinBonvoyPopup = useCallback(
    () => setShowJoinBonvoyPopup(false),
    [setShowJoinBonvoyPopup],
  );

  const handleExploreStays = useCallback((searchData) => {
    console.log("Explore stays clicked with data:", searchData);
    // Here you would typically navigate to a search results page
    // or trigger a search API call
  }, []);

  return (
    <>
      <div className={styles.container}>
        <HeroPassion selectedPassions={selectedPassions} />
        <div className={styles.content}>
          <div className={styles.grid}>
            {showCollectionsModule && (
              <CollectionsModule onSaveSuccess={handleSaveSuccess} />
            )}
            <div className={styles.membershipTierDestination}>
              {acid && (
                <MembershipTier
                  tierName={membershipTierData?.tierName}
                  nextTierName={membershipTierData?.nextTierName}
                  currentProgress={membershipTierData?.currentProgress}
                  targetProgress={membershipTierData?.targetProgress}
                  loading={profileLoading}
                />
              )}
              {recentlyViewedPropertyId && (
                <DestinationSearch
                  id="discover-destination"
                  onExploreStays={handleExploreStays}
                  heroImage={destinationImageUrl}
                  recentlyViewedProperty={recentlyViewedProperty}
                  passion={currentPassion}
                  initialDestination={initialDestination}
                  loading={(profileLoading && !!acid) || hotelsLoading}
                />
              )}
            </div>
            <HotelsCarousel />
            <HobbyCarousel />
            <PassionCarousel />
            <MomentPassion />
          </div>
        </div>
      </div>

      {showJoinBonvoyPopup && (
        <JoinBonvoyPopup onClose={onCloseJoinBonvoyPopup} />
      )}
    </>
  );
};
